import { isProduction } from '../config/api.config';

/**
 * Google Analytics, loaded only after the visitor accepts it on the cookie banner.
 * Until then no gtag.js request is made and no _ga cookie is set.
 */
export const GA_MEASUREMENT_ID = 'G-D6KF2WSC2K';

export type AnalyticsConsent = 'granted' | 'denied';

const STORAGE_KEY = 'analyticsConsent';
/** The banner asks again after a year, as EU regulators recommend. */
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const CONSENT_CHANGE_EVENT = 'analytics-consent-change';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let loaded = false;

/** The saved choice, or null when the visitor has not chosen yet (or the choice expired). */
export const getAnalyticsConsent = (): AnalyticsConsent | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { choice, at } = JSON.parse(raw) as { choice?: string; at?: string };
    if (choice !== 'granted' && choice !== 'denied') return null;
    if (!at || Date.now() - new Date(at).getTime() > CONSENT_MAX_AGE_MS) return null;
    return choice;
  } catch {
    return null;
  }
};

const setDisabled = (disabled: boolean) => {
  (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
};

const loadAnalytics = () => {
  setDisabled(false);
  if (loaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    return;
  }
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js reads the Arguments object itself, not an array
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

/** Removes the _ga cookies GA set on this host or any parent domain (it uses the top one). */
const deleteAnalyticsCookies = () => {
  const names = document.cookie.split(';').map((c) => c.split('=')[0].trim()).filter((n) => n.startsWith('_ga'));
  const parts = window.location.hostname.split('.');
  const domains = [''].concat(parts.slice(0, -1).map((_, i) => `; domain=.${parts.slice(i).join('.')}`));
  names.forEach((name) => domains.forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
  }));
};

const disableAnalytics = () => {
  setDisabled(true);
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  deleteAnalyticsCookies();
};

/** Loads GA at startup when the visitor accepted it earlier (production builds only). */
export const initAnalytics = () => {
  if (isProduction && getAnalyticsConsent() === 'granted') loadAnalytics();
};

export const setAnalyticsConsent = (choice: AnalyticsConsent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
  } catch {
    // Private mode: the choice holds for this visit only
  }
  if (choice === 'granted') {
    if (isProduction) {
      loadAnalytics();
      trackPageView();
    }
  } else {
    disableAnalytics();
  }
  window.dispatchEvent(new CustomEvent<AnalyticsConsent>(CONSENT_CHANGE_EVENT, { detail: choice }));
};

export type TodoSurface = 'list' | 'detail' | 'timely' | 'home' | 'onboarding';
export type AiAssistAction = 'suggest_subtasks' | 'summarize' | 'improve' | 'completion_note';

/**
 * Every product event and its parameters. Parameters are fixed values, numbers or booleans only,
 * so user content (titles, descriptions, emails) cannot end up in Google Analytics.
 */
export interface AnalyticsEvents {
  sign_up: { method: 'password' | 'google' };
  login: { method: 'password' | 'google' };
  email_verified: Record<string, never>;
  goal_created: { method: 'manual' | 'ai' };
  ai_plan_requested: { from: 'modal' | 'drafts' };
  ai_plan_questions_shown: { count: number };
  ai_plan_questions_result: { answered: number; skipped: boolean };
  ai_plan_applied: { subgoal_count: number; todo_count: number };
  todo_created: { source: 'subgoal' | 'timely_quick' };
  todo_completed: { surface: TodoSurface; with_note: boolean };
  ai_assist_used: { action: AiAssistAction };
  ai_assist_applied: { action: AiAssistAction };
  ai_quota_exhausted: { pool: string };
  timer_started: { surface: TodoSurface };
  pomodoro_completed: Record<string, never>;
  integration_connected: { type: 'token' | 'oauth' };
  account_deleted: Record<string, never>;
  account_exported: Record<string, never>;
  // The first-goal flow without an account (T-159; same names and parameters as the app's
  // onboarding, so one funnel covers both)
  // source: the link's src (promo_hero, …) or direct (T-165)
  onboarding_started: { source: string };
  onboarding_demo_submitted: { source: 'example' | 'typed' };
  onboarding_questions_answered: { answered: number; total: number; ai_questions: number };
  onboarding_questions_skipped: { answered: number };
  onboarding_plan_ready: { seconds: number; resumed: boolean; sub_goals: number; tasks: number };
  onboarding_demo_failed: { kind: 'busy' | 'running' | 'offline' | 'tooLong' | 'error' };
  onboarding_sample_used: { matched: boolean };
  onboarding_plan_revised: { kind: 'chip' | 'custom' };
  onboarding_signup_method: { method: 'google' | 'email' | 'signin' };
  onboarding_plan_transferred: { sample: boolean };
  onboarding_completed: { action: 'app' | 'first_task' };
  onboarding_abandoned: { step: string };
}

declare global {
  interface Window {
    /** Outside production, tracked events are collected here for e2e tests instead of sent. */
    __riselioEvents?: Array<{ name: string; params: object }>;
  }
}

/** Sends a product event; does nothing without consent. */
export const trackEvent = <K extends keyof AnalyticsEvents>(name: K, params: AnalyticsEvents[K]) => {
  if (!isProduction) {
    (window.__riselioEvents = window.__riselioEvents || []).push({ name, params });
    return;
  }
  if (!loaded || getAnalyticsConsent() !== 'granted') return;
  window.gtag?.('event', name, params);
};

/** UI language of the visitor, attached to later events. */
export const setAnalyticsUserProperties = (properties: { ui_language: string }) => {
  if (!loaded || getAnalyticsConsent() !== 'granted') return;
  window.gtag?.('set', 'user_properties', properties);
};

/** Sends a page_view for the current hash route; does nothing without consent. */
export const trackPageView = () => {
  if (!loaded || getAnalyticsConsent() !== 'granted') return;
  window.gtag?.('event', 'page_view', {
    send_to: GA_MEASUREMENT_ID,
    page_path: window.location.hash.replace('#', '') || '/',
    page_location: window.location.href,
    page_title: document.title,
  });
};
